import { useRef, useState } from 'preact/hooks';
import type { AchievementDef, Content } from '../../../data/schema';
import type { Meta } from '../../../net/progress';
import { useEscape } from '../useEscape';
import { useInitialFocus } from '../useInitialFocus';

type Filter = 'all' | 'done' | 'todo';
const FILTERS: { id: Filter; label: string }[] = [{ id: 'all', label: 'Tous' }, { id: 'done', label: 'Obtenus' }, { id: 'todo', label: 'À faire' }];

/** « JJ/MM/AAAA » en heure locale, formaté à la main (indépendant de la locale du navigateur) ; null si la date est invalide. */
export function formatDate(at: string): string | null {
  const d = new Date(at);
  if (Number.isNaN(d.getTime())) return null;
  const two = (n: number) => String(n).padStart(2, '0');
  return `${two(d.getDate())}/${two(d.getMonth() + 1)}/${d.getFullYear()}`;
}

interface Props { meta: Meta; content: Content; onBack: () => void }

/** Page « Succès » du hub : grille de cartes avec filtre Tous / Obtenus / À faire. */
export function Achievements({ meta, content, onBack }: Props) {
  const panel = useRef<HTMLDivElement>(null);
  const [filter, setFilter] = useState<Filter>('all');
  useEscape(onBack);
  useInitialFocus(panel, '.achievements-back');

  const all = Object.values(content.achievements);
  const got = new Map(meta.achievements.filter((a) => content.achievements[a.id]).map((a) => [a.id, a.at]));
  const shown = all.filter((a) => (filter === 'all' ? true : filter === 'done' ? got.has(a.id) : !got.has(a.id)));

  const onFilterKey = (e: KeyboardEvent) => {
    if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight' && e.key !== 'ArrowUp' && e.key !== 'ArrowDown') return;
    e.preventDefault();
    const i = FILTERS.findIndex((f) => f.id === filter);
    const step = e.key === 'ArrowRight' || e.key === 'ArrowDown' ? 1 : -1;
    const to = FILTERS[(i + step + FILTERS.length) % FILTERS.length]!;
    setFilter(to.id);
    requestAnimationFrame(() => panel.current?.querySelector<HTMLElement>(`[role="radio"][data-filter="${to.id}"]`)?.focus());
  };

  return (
    <div class="panel achievements-panel" role="dialog" aria-label="Succès" ref={panel}>
      <div class="achievements-head">
        <button class="achievements-back" type="button" onClick={onBack}>Retour</button>
        <h2>Succès</h2>
        <span class="achievements-count"><b>{got.size}</b> / {all.length}</span>
        <div class="achievements-filter" role="radiogroup" aria-label="Filtrer les succès" onKeyDown={onFilterKey}>
          {FILTERS.map((f) => (
            <button key={f.id} type="button" role="radio" data-filter={f.id} aria-checked={filter === f.id} tabIndex={filter === f.id ? 0 : -1} class={filter === f.id ? 'is-active' : ''} onClick={() => setFilter(f.id)}>{f.label}</button>
          ))}
        </div>
      </div>
      <ul class="achievements-grid">
        {shown.map((a) => <Card key={a.id} def={a} at={got.get(a.id)} content={content} />)}
      </ul>
    </div>
  );
}

function Card({ def, at, content }: { def: AchievementDef; at: string | undefined; content: Content }) {
  const done = at !== undefined;
  const secret = def.hidden && !done;
  const unlock = def.unlocks ? content.unlockables[def.unlocks] : undefined;
  const date = done ? formatDate(at) : null;
  return (
    <li class={`achievement ${done ? 'is-done' : 'is-todo'}`} data-achievement={def.id}>
      {secret
        ? <span class="achievement-icon is-secret" aria-hidden="true">?</span>
        : <img class={`achievement-icon${done ? '' : ' is-grey'}`} src={`/assets/${def.icon}.png`} alt="" />}
      <div class="achievement-body">
        <b>{secret ? '???' : def.name}</b>
        <p>{secret ? 'Succès secret' : def.description}</p>
        <div class="achievement-meta">
          {!secret && <span class="ogrines">+{def.ogrines} Ogrines</span>}
          <span class="date">{done ? (date ? `Obtenu le ${date}` : 'Obtenu') : 'Verrouillé'}</span>
        </div>
        {!secret && unlock && <span class="unlocks">Débloque : {unlock.name}</span>}
      </div>
    </li>
  );
}
